import { motion, AnimatePresence } from "framer-motion";
import { Trophy } from "lucide-react";

/**
 * Token board — sits at the top of the Match/Sort runner. Shows N empty slots
 * (matching the count of generated tokens); fills one slot per correct answer.
 * When the board is full, `onFull` is fired so the parent can trigger a
 * celebration + reveal the reinforcer big. Reset happens automatically after.
 *
 * All props are optional — if no reinforcer set, the board renders nothing so
 * the classic no-token flow still works.
 */
export default function TokenBoard({
  earned = 0,
  tokens = [],
  reinforcerImage = "",
  reinforcerLabel = "",
  celebrating = false,
}) {
  if (tokens.length === 0) return null;
  const total = tokens.length;
  const tokenSrc = tokens[0]; // all tokens are identical (ABA-standard)

  return (
    <div
      className="bg-gradient-to-r from-amber-50 via-white to-emerald-50 border-b border-amber-100 px-4 sm:px-6 py-3 flex items-center gap-4 sm:gap-6"
      data-testid="token-board"
    >
      <div className="shrink-0 hidden sm:block">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Working for</div>
        <div className="font-display font-extrabold text-slate-900 leading-tight" data-testid="token-board-reinforcer-label">
          {reinforcerLabel || "Reward"}
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center gap-2 sm:gap-3">
        <AnimatePresence>
          {Array.from({ length: total }).map((_, i) => {
            const filled = i < earned;
            return (
              <motion.div
                key={`slot-${total}-${i}`}
                initial={false}
                animate={filled && !celebrating ? { scale: [0.5, 1.15, 1], rotate: [0, 6, 0] } : { scale: 1 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-colors ${
                  filled ? "bg-amber-100 ring-2 ring-amber-300 shadow-sm" : "bg-slate-100 border-2 border-dashed border-slate-300"
                }`}
                data-testid={`token-slot-${i}${filled ? "-filled" : ""}`}
              >
                {filled && tokenSrc && (
                  <img src={tokenSrc} alt="" className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-lg" />
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <div className="shrink-0 flex items-center gap-3">
        {reinforcerImage ? (
          <motion.div
            animate={celebrating ? { scale: [1, 1.35, 1.1], rotate: [0, -8, 8, 0] } : {}}
            transition={{ duration: 0.8 }}
            className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden ring-2 ring-emerald-300 bg-white shadow-md"
            data-testid="token-board-reinforcer-image"
          >
            <img src={reinforcerImage} alt={reinforcerLabel} className="w-full h-full object-cover" />
          </motion.div>
        ) : (
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-emerald-100 ring-2 ring-emerald-300 flex items-center justify-center">
            <Trophy className="w-6 h-6 text-emerald-700" />
          </div>
        )}
      </div>

      {celebrating && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center z-[60]"
          data-testid="token-board-celebration"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl border border-emerald-200 shadow-2xl px-8 py-6 flex flex-col items-center max-w-sm text-center"
          >
            <div className="text-4xl mb-2">🎉</div>
            {reinforcerImage && (
              <img
                src={reinforcerImage}
                alt={reinforcerLabel}
                className="w-32 h-32 object-cover rounded-2xl mb-3 ring-4 ring-amber-200"
              />
            )}
            <div className="font-display text-xl font-extrabold text-slate-900">You earned it!</div>
            <div className="text-sm text-slate-600 mt-1">
              Time for <span className="font-bold text-emerald-700">{reinforcerLabel}</span>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
